import { Switch } from '@cfa/react-components';
import { Lock } from '@cfa/system-icons';
import { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { actions as cartActions } from '../../reducer';
import Icon from '../../../components/Icon';
import QuantityStepper from '../../../components/QuantityStepper';
import { formatPrice } from '../../../util/format';
import { ensureValueIsWholeNumber } from '../../../util/validate';

interface Props {
  className?: string;
  cartItemId: string;
  item: ModifierView;
  maximum?: number;
  modifierGroupingCount?: number;
}

const Modifier: React.FC<Props> = ({
  className = 'modifier',
  cartItemId,
  item,
  maximum = 999,
  modifierGroupingCount = 0,
}) => {
  const dispatch = useDispatch();

  const {
    name,
    imgUrl,
    leadTime,
    disabled: itemDisabled,
    quantity,
    tag,
    comboTag,
    groupType,
    price,
    toggleable,
    defaultAvailable,
    calories,
    type,
  } = item;

  const toggleableChecked = defaultAvailable ? !quantity : !!quantity;

  const [stepperValue, setStepperValue] = useState(quantity);

  useEffect(() => {
    setStepperValue(quantity);
  }, [quantity]);

  const onQuantityChange = (newQuantity: number) => {
    if (newQuantity > maximum - modifierGroupingCount + (quantity ?? 0)) {
      return;
    }

    setStepperValue(newQuantity);
    const qty = ensureValueIsWholeNumber(newQuantity.toString());
    dispatch(cartActions.updateModifierQuantity({ id: cartItemId, tag, quantity: qty, comboTag }));
  };

  const onAddToCart = () => {
    if (itemDisabled || (!toggleable && modifierGroupingCount >= maximum)) {
      return;
    }
    dispatch(cartActions.addModifier({ id: cartItemId, tag, comboTag }));
  };

  return (
    <StyledModifier className={className} onKeyDown={() => {}}>
      <div className="image-wrapper" onClick={onAddToCart} role="presentation">
        <img
          alt={name}
          className={itemDisabled ? 'image disabled' : 'image'}
          data-cy={`${groupType}-${tag}`}
          src={imgUrl}
        />
        {itemDisabled && <Lock className="lock-icon" color="#DD0031" size={24} />}
      </div>
      <div className={itemDisabled ? 'name-wrapper disabled' : 'name-wrapper'} onClick={onAddToCart}>
        <div className="name">{name}</div>
        <div>
          <span className="price" data-cy={`${tag}-price`}>
            {formatPrice(type !== 'NO_REFUND_RECIPE' ? price : 0)}
          </span>
          {typeof calories === 'number' && (
            <span className="calories" data-cy={`${tag}-calories`}>
              {' '}
              | {calories} cal
            </span>
          )}
        </div>
      </div>
      {leadTime && (
        <div className="leadtime-details">
          <Icon height="17" margin="0 2px 2px 0" width="17">
            warning
          </Icon>
          <div className="leadTime">{`Lead time: ${leadTime}h`}</div>
        </div>
      )}
      {toggleable ? (
        <Switch checked={toggleableChecked} onClick={onAddToCart} />
      ) : (
        <QuantityStepper
          className="modifier-quantity-stepper"
          data-cy={`quantity-${tag}`}
          disabled={itemDisabled}
          max={maximum - modifierGroupingCount + (quantity ?? 0)}
          min={0}
          onQuantityChange={onQuantityChange}
          quantity={stepperValue}
          useTrashCan={false}
        />
      )}
    </StyledModifier>
  );
};

const StyledModifier = styled.div`
  display: flex;
  flex: 0 1 24.5%;
  height: 100%;
  box-sizing: border-box;
  padding-bottom: 8px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  & .name-wrapper {
    flex-grow: 1;
    padding: 0 10px;

    &.disabled {
      color: #a7acaf;
    }

    &:hover {
      cursor: pointer;
    }
  }

  & .image-wrapper {
    position: relative;
    height: 42px;
    width: 42px;

    &:hover {
      cursor: pointer;
    }
  }

  & .lock-icon {
    position: absolute;
    float: right;
  }

  & .image {
    max-width: 100%;
    max-height: 100%;
    margin: 0 auto;

    &.disabled {
      opacity: 0.5;
    }
  }

  & .name {
    font: ${(props) => props.theme.smallBoldFont};
  }

  & .price {
    font: ${(props) => props.theme.smallTextFont};
  }

  & .calories {
    font: ${(props) => props.theme.smallTextFont};
  }

  & .leadtime-details {
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  & .modifier-quantity-stepper {
    max-width: 60px;
  }

  @media (max-width: ${(props) => props.theme.small}) {
    flex: 0 1 50%;
  }

  @media (min-width: ${(props) => props.theme.phone}) {
    flex: 0 1 25%;

    & .toggleable:hover {
      border: 1px solid ${(props) => props.theme.colors.accent} !important;
      box-shadow: 0 0 5px 1px gainsboro;
    }
  }

  @media (min-width: ${(props) => props.theme.tablet}) {
    flex: 0 1 20%;
  }

  @media (min-width: ${(props) => props.theme.desktop}) {
    flex: 0 1 20%;
  }
`;

export default memo(Modifier);
